import { zodResolver } from '@hookform/resolvers/zod';
import { useMemo } from 'react';
import { useForm, useWatch } from 'react-hook-form';

import { DateRangePicker } from '@/components/common/date-picker';
import { Drawer } from '@/components/feedback/drawer';
import { MultiSelectField } from '@/components/forms';
import { useMetaCategories } from '@/features/devices';
import { useOrderCities } from '@/features/orders';
import { useCursorSelect } from '@/hooks/use-cursor-select';
import { appTzLabel, fromDateRange, isAfterAppToday, toDayjsRange } from '@/lib/date';

import { listWarrantyDevices } from '../api';
import { toDefaults, toFilters } from '../filters';
import { CLAIM_STATUS_CHIP, WARRANTY_REASONS, WARRANTY_STATUS_CHIP } from '../format';
import { warrantyKeys } from '../hooks';
import { warrantyFiltersSchema } from '../schemas';
import type {
  ClaimStatus,
  WarrantyClaimListFilters,
  WarrantyDeviceOption,
  WarrantyFilterFormValues,
  WarrantyReason,
  WarrantyStatus,
} from '../types';

import styles from './warranty-filter-drawer.module.scss';

/** The three fixed-enum sources reuse the table's own label maps, so a filter option can never
 *  drift from the chip/text the matching row renders. */
const REASON_OPTIONS = (Object.keys(WARRANTY_REASONS) as WarrantyReason[]).map((value) => ({
  value,
  label: WARRANTY_REASONS[value],
}));
const CLAIM_STATUS_OPTIONS = (Object.keys(CLAIM_STATUS_CHIP) as ClaimStatus[]).map((value) => ({
  value,
  label: CLAIM_STATUS_CHIP[value].label,
}));
const WARRANTY_STATUS_OPTIONS = (Object.keys(WARRANTY_STATUS_CHIP) as WarrantyStatus[]).map(
  (value) => ({ value, label: WARRANTY_STATUS_CHIP[value].label }),
);

const toOption = (o: WarrantyDeviceOption) => ({ value: o.id, label: o.name });

type DateRangeFieldsetProps = {
  label: string;
  from: string | null;
  to: string | null;
  error?: string;
  onChange: (from: string | null, to: string | null) => void;
};

/** One DateRangePicker per range (Figma 350:290001 / 350:290004), bound to the two scalar draft
 *  fields rather than a tuple — that is the shape `filters.ts` already puts on the wire. Not a
 *  `<FormField>`, so it carries its own label + error. */
function DateRangeFieldset({ label, from, to, error, onChange }: DateRangeFieldsetProps) {
  // ADO 120504: a filter edge is an application-calendar (Eastern) date, so say so once here —
  // both ranges render through this fieldset.
  const tzLabel = appTzLabel(label);
  return (
    <fieldset className={styles.fieldset}>
      <legend className={styles.legend}>{tzLabel}</legend>
      <DateRangePicker
        aria-label={tzLabel}
        format="MM/DD/YYYY"
        disabledDate={isAfterAppToday}
        style={{ width: '100%' }}
        status={error ? 'error' : undefined}
        value={toDayjsRange(from && to ? [from, to] : null)}
        onChange={(range) => {
          const iso = fromDateRange(range);
          onChange(iso?.[0] ?? null, iso?.[1] ?? null);
        }}
      />
      {error ? <p className={styles.error}>{error}</p> : null}
    </fieldset>
  );
}

export type WarrantyFilterDrawerProps = {
  open: boolean;
  /** Last applied filters; the draft re-seeds from this on each open. */
  value: WarrantyClaimListFilters;
  onApply: (filters: WarrantyClaimListFilters) => void;
  onClose: () => void;
};

type BodyProps = Omit<WarrantyFilterDrawerProps, 'open'>;

function FilterBody({ value, onApply, onClose }: BodyProps) {
  const draft = toDefaults(value);
  const form = useForm<WarrantyFilterFormValues>({
    resolver: zodResolver(warrantyFiltersSchema),
    defaultValues: {
      ...draft,
      // `toDefaults` maps unset date bounds to `undefined`; antd clears to `null`, and an
      // `undefined` RHF value silently reverts to the default — so coalesce, never hardcode
      // `null` (that would clobber an applied value when the drawer reopens).
      orderDateFrom: draft.orderDateFrom ?? null,
      orderDateTo: draft.orderDateTo ?? null,
      claimDateFrom: draft.claimDateFrom ?? null,
      claimDateTo: draft.claimDateTo ?? null,
    },
    mode: 'onBlur',
  });

  // Filter options come from the existing shared product/order meta endpoints — the pushed
  // Postman stub states warranty adds no meta routes of its own.
  // Server-searched and cursor-paged, the same call the Replacements queue makes. Deliberately
  // NOT the bundles feature's `useMetaDevices`, whose `status=active&bundleEligible=true` hid
  // every non-bundle-eligible device from this filter (ADO 118533).
  const deviceSelect = useCursorSelect({
    queryKey: warrantyKeys.metaDevices,
    fetchPage: async ({ search, cursor, limit }) => {
      const page = await listWarrantyDevices({ search, cursor, limit });
      return {
        data: page.data,
        nextCursor: page.meta.nextCursor,
        hasNextPage: page.meta.hasNextPage,
      };
    },
    toOption,
  });
  const categories = useMetaCategories();
  const categoryOptions = useMemo(
    () => (categories.data ?? []).map((c) => ({ value: c.id, label: c.name })),
    [categories.data],
  );
  const cities = useOrderCities();
  const cityOptions = useMemo(
    () => (cities.data ?? []).map((c) => ({ value: c, label: c })),
    [cities.data],
  );

  const handleApply = form.handleSubmit((values) => {
    onApply(toFilters(values));
    onClose();
  });

  // The From<=To guard still runs so Apply disables, even though the range picker itself blocks
  // an inverted pick through the calendar.
  const [orderedFrom, orderedTo, claimedFrom, claimedTo] = useWatch({
    control: form.control,
    name: ['orderDateFrom', 'orderDateTo', 'claimDateFrom', 'claimDateTo'],
  });
  const orderedError = form.formState.errors.orderDateTo?.message;
  const claimedError = form.formState.errors.claimDateTo?.message;

  // The shared Drawer already defaults to the Figma panel width (464px, node 30:420338), so no
  // `width` prop is passed.
  return (
    <Drawer
      open
      onClose={onClose}
      title="Filter"
      primaryText="Apply"
      onPrimary={handleApply}
      primaryDisabled={Boolean(orderedError || claimedError)}
      cancelText="Cancel"
      onCancel={onClose}
    >
      <MultiSelectField
        name="deviceIds"
        control={form.control}
        label="Device"
        placeholder="Select"
        showSearch={deviceSelect.showSearch}
        loading={deviceSelect.loading}
        onPopupScroll={deviceSelect.onPopupScroll}
        options={deviceSelect.options}
      />

      <MultiSelectField
        name="categoryIds"
        control={form.control}
        label="Device Category"
        placeholder="Select"
        loading={categories.isLoading}
        options={categoryOptions}
      />

      <DateRangeFieldset
        label="Order Date Range"
        from={orderedFrom}
        to={orderedTo}
        error={orderedError}
        onChange={(from, to) => {
          form.setValue('orderDateFrom', from, { shouldValidate: true });
          form.setValue('orderDateTo', to, { shouldValidate: true });
        }}
      />

      <MultiSelectField
        name="cities"
        control={form.control}
        label="City"
        placeholder="Select"
        loading={cities.isLoading}
        options={cityOptions}
      />

      <MultiSelectField
        name="warrantyStatuses"
        control={form.control}
        label="Warranty Status"
        placeholder="Select"
        options={WARRANTY_STATUS_OPTIONS}
      />

      <MultiSelectField
        name="claimStatuses"
        control={form.control}
        label="Claim Status"
        placeholder="Select"
        options={CLAIM_STATUS_OPTIONS}
      />

      <MultiSelectField
        name="reasons"
        control={form.control}
        label="Reason for Warranty Claim"
        placeholder="Select"
        options={REASON_OPTIONS}
      />

      <DateRangeFieldset
        label="Warranty Claim Date Range"
        from={claimedFrom}
        to={claimedTo}
        error={claimedError}
        onChange={(from, to) => {
          form.setValue('claimDateFrom', from, { shouldValidate: true });
          form.setValue('claimDateTo', to, { shouldValidate: true });
        }}
      />
    </Drawer>
  );
}

export function WarrantyFilterDrawer({ open, ...rest }: WarrantyFilterDrawerProps) {
  if (!open) {
    return (
      <Drawer open={false} onClose={rest.onClose}>
        {null}
      </Drawer>
    );
  }
  // Unmount on close; remounts fresh on open so the draft re-seeds from the last applied `value`.
  return <FilterBody {...rest} />;
}
